/* eslint-disable react/require-default-props -- defaults via destructuring; no defaultProps (React 19) */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import './Badge.scss';
import Button from '../Button';
import { FlexRow } from '../GridsAndLayouts/GridsAndLayouts';
import { CloseIcon } from '../Icons/CloseIcon';
import Typography from '../Typography';

const SIZE_ALIASES = {
  small: 'sm',
  medium: 'md',
  large: 'lg',
};

function normalizeSize(size) {
  if (!size) return 'md';
  return SIZE_ALIASES[size] || size;
}

/** Legacy `theme` → semantic `variant` (Figma intents). */
const THEME_TO_VARIANT = {
  gray: 'waiting',
  green: 'positive',
  red: 'negative',
  yellow: 'notice',
  blue: 'information',
  indigo: 'information',
  purple: 'primary',
};

function resolveVariant({ variant, theme }) {
  if (variant) return variant;
  return THEME_TO_VARIANT[theme] || 'waiting';
}

const BADGE_SIZE_TO_TYPOGRAPHY = {
  sm: 'bodyExtraSmall',
  md: 'bodySmall',
  lg: 'bodyMedium',
};

/**
 * Design-system badge (pill / rounded).
 * - Prefer `variant` + `emphasis`; `theme` is kept for backward compatibility.
 * - Optional `icon` slot before the label.
 */
const Badge = ({
  variant: variantProp,
  emphasis = 'subtle',
  type = 'basic',
  size: sizeProp = 'md',
  theme = 'gray',
  icon,
  dot = false,
  removable = false,
  bordered = false,
  transparent = false,
  onDismiss,
  children,
  className = '',
  ...props
}) => {
  const size = normalizeSize(sizeProp);
  const variant = resolveVariant({ variant: variantProp, theme });

  const rootClass = classNames(
    'ds-badge',
    `ds-badge--${variant}`,
    `ds-badge--emphasis-${emphasis}`,
    `ds-badge--size-${size}`,
    type === 'basic' && 'ds-badge--pill',
    type === 'rounded' && 'ds-badge--rounded',
    dot && 'ds-badge--with-dot',
    icon && 'ds-badge--with-icon',
    removable && 'ds-badge--removable',
    bordered && 'ds-badge--bordered',
    transparent && 'ds-badge--transparent',
    className
  );

  const showLabel = children != null && !(typeof children === 'string' && children.trim() === '');

  return (
    <FlexRow
      inline
      className={rootClass}
      align="center"
      justify="center"
      gap="var(--ds-space-2)"
      fullWidth={false}
      {...props}
    >
      {dot ? (
        <FlexRow inline className="ds-badge__dot" align="center" justify="center" fullWidth={false} aria-hidden />
      ) : null}
      {icon ? (
        <FlexRow
          inline
          className="ds-badge__icon"
          align="center"
          justify="center"
          fullWidth={false}
          aria-hidden
          style={{ lineHeight: 0 }}
        >
          {icon}
        </FlexRow>
      ) : null}
      {showLabel ? (
        <Typography
          component="span"
          variant={BADGE_SIZE_TO_TYPOGRAPHY[size]} // owns font-size + line-height
          fontWeight="medium" // font-weight: 500
          color="inherit" // inherits badge color
          className="ds-badge__label"
        >
          {children}
        </Typography>
      ) : null}
      {removable ? (
        <Button
          variant="tertiary"
          size="sm"
          type="button"
          className="ds-badge__remove"
          onClick={onDismiss}
          aria-label="Dismiss badge"
          icon={<CloseIcon size={12} strokeWidth={1.5} />}
        />
      ) : null}
    </FlexRow>
  );
};

Badge.propTypes = {
  /** Semantic intent; overrides legacy `theme` when set. */
  variant: PropTypes.oneOf(['positive', 'negative', 'notice', 'information', 'waiting', 'primary']),
  /** Subtle: tinted background + solid intent color. Intense: solid fill + white label/icon. */
  emphasis: PropTypes.oneOf(['subtle', 'intense']),
  /** `basic`: pill (24px radius). `rounded`: 8px radius (dense layouts). */
  type: PropTypes.oneOf(['basic', 'rounded']),
  /** `sm` | `md` | `lg`; also accepts `small` | `medium` | `large`. */
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'small', 'medium', 'large']),
  /** @deprecated Prefer `variant`. Still supported for existing screens. */
  theme: PropTypes.oneOf(['gray', 'red', 'yellow', 'green', 'blue', 'indigo', 'purple']),
  /** Optional leading icon or glyph. */
  icon: PropTypes.node,
  dot: PropTypes.bool,
  /** When true, renders a 1px outline matched to the badge intent color. */
  bordered: PropTypes.bool,
  /** When true, removes the tinted background (use with `bordered` for outline-only badges). */
  transparent: PropTypes.bool,
  removable: PropTypes.bool,
  onDismiss: PropTypes.func,
  children: PropTypes.node,
  className: PropTypes.string,
};

export default Badge;
